import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { MenuResponse } from '@scan2eat/shared';
import { formatMoney } from '@scan2eat/shared';
import { SESSION_TOKEN_STORAGE_KEY } from '@scan2eat/shared';

import { readStoredValue } from '../lib/storage.js';
import { apiFetch } from '../api/client.js';
import { useCartStore } from '../store/cart.js';
import { useRealtimeSync } from '../realtime/useRealtimeSync.js';
import { getDisplayContext } from '../lib/display-context.js';
import { labels } from '../labels.js';
import { ensureOrderableSession } from '../lib/session.js';
import { BottomNav } from '../components/BottomNav.js';
import { CartBar } from '../components/CartBar.js';
import { CategoryTabs } from '../components/CategoryTabs.js';
import { ConnectionBanner } from '../components/ConnectionBanner.js';
import { EmptyState } from '../components/EmptyState.js';
import { Icon } from '../components/Icon.js';
import { Logo } from '../components/Logo.js';
import { MenuItemCard } from '../components/MenuItemCard.js';
import styles from './MenuPage.module.css';

export function MenuPage() {
  const [menu, setMenu] = useState<MenuResponse | null>(null);
  const [activeCat, setActiveCat] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionClosed, setSessionClosed] = useState(false);
  const [needsRescan, setNeedsRescan] = useState(false);
  const [pinned, setPinned] = useState(false);
  const cart = useCartStore();
  const itemCount = useCartStore((s) => s.itemCount());
  const totalMinor = useCartStore((s) => s.totalMinor());
  const token = readStoredValue(SESSION_TOKEN_STORAGE_KEY);
  const display = getDisplayContext();
  const sectionRefs = useRef<Array<HTMLElement | null>>([]);
  const heroRef = useRef<HTMLElement | null>(null);
  const suppressSpy = useRef(false);

  const load = useCallback(() => {
    return apiFetch<MenuResponse>('/public/menu')
      .then((data) => { setMenu(data); setError(null); })
      .catch(() => setError(labels.errors.network))
      .finally(() => setLoading(false));
  }, []);

  // Entrer dans le menu, c'est vouloir commander. On s'assure donc d'une
  // session ouverte : apres un paiement elle est close, et l'application en
  // rouvre une a partir du QR code memorise plutot que d'exiger un nouveau
  // scan. Voir lib/session.ts.
  useEffect(() => {
    let cancelled = false;
    void ensureOrderableSession().then((state) => {
      if (cancelled) return;
      if (state === 'rescan') {
        setNeedsRescan(true);
        setLoading(false);
        return;
      }
      void load();
    });
    return () => { cancelled = true; };
  }, [load]);

  const onResync = useCallback(() => { load(); }, [load]);

  const { socket, connected } = useRealtimeSync(token, onResync);

  useEffect(() => {
    if (!socket) return;
    const onClosed = (e: { payload: { reason: string } }) => {
      if (e.payload.reason === 'PAYMENT_COMPLETED') {
        // La commande vient d'etre payee : le panier n'a plus de destination,
        // et la session est close. On en rouvre une pour que le convive
        // puisse commander a nouveau sans rescanner. Voir lib/session.ts.
        useCartStore.getState().clear();
        void ensureOrderableSession().then((state) => {
          if (state === 'rescan') setNeedsRescan(true);
        });
        return;
      }
      setSessionClosed(true);
    };
    const onAvail = (e: { payload: { itemId: string; isAvailable: boolean } }) => {
      setMenu((prev) => {
        if (!prev) return prev;
        return {
          categories: prev.categories.map((c) => ({
            ...c,
            items: c.items.map((i) =>
              i.id === e.payload.itemId ? { ...i, isAvailable: e.payload.isAvailable } : i,
            ),
          })),
        };
      });
    };
    socket.on('session:closed' as never, onClosed as never);
    socket.on('menu:availability_changed' as never, onAvail as never);
    return () => {
      socket.off('session:closed' as never, onClosed as never);
      socket.off('menu:availability_changed' as never, onAvail as never);
    };
  }, [socket]);

  // Scroll-spy, and the pinned state of the category bar once the hero is gone.
  useEffect(() => {
    if (loading || !menu || menu.categories.length === 0) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const hero = heroRef.current;
        setPinned(hero ? hero.getBoundingClientRect().bottom <= 0 : false);
        if (suppressSpy.current) return;
        const offset = window.scrollY + 140;
        let current = 0;
        sectionRefs.current.forEach((el, i) => {
          if (el && el.offsetTop <= offset) current = i;
        });
        setActiveCat(current);
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [loading, menu]);

  const selectCat = (i: number) => {
    setActiveCat(i);
    suppressSpy.current = true;
    sectionRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.setTimeout(() => { suppressSpy.current = false; }, 800);
  };

  const rescan = () => {
    localStorage.removeItem(SESSION_TOKEN_STORAGE_KEY);
    cart.clear();
    window.location.href = '/scan';
  };

  if (needsRescan || sessionClosed) {
    return (
      <main className={`client-screen ${styles.centered}`}>
        <EmptyState
          icon="scan"
          title={labels.errors.sessionClosed}
          text={needsRescan ? labels.errors.orderAgainAfterPayment : labels.errors.thanks}
        >
          <button onClick={rescan} className="btn-primary btn-lg">
            <Icon name="scan" size={20} />
            {labels.app.rescan}
          </button>
        </EmptyState>
      </main>
    );
  }

  const cats = menu?.categories ?? [];
  const currency =
    display?.restaurant.currency ??
    cats.flatMap((c) => c.items)[0]?.currency ??
    'EUR';
  const restaurantName = display?.restaurant.name ?? labels.menu.title;

  return (
    <div className={`client-screen ${styles.screen}`}>
      <ConnectionBanner connected={connected || loading} />

      {/* Photo de la salle en fond, voile sombre pour garder le texte lisible.
          Sans photo (roomPhotoUrl nul), le fond uni de .hero s'applique tel
          quel. Voir docs/adr/0025, section « Un second usage ». */}
      <header
        ref={heroRef}
        className={styles.hero}
        style={
          display?.restaurant.roomPhotoUrl
            ? {
                backgroundImage: `linear-gradient(180deg, rgba(51,25,13,0.45), rgba(51,25,13,0.85)), url("${display.restaurant.roomPhotoUrl}")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : undefined
        }
      >
        <div className={styles.heroTop}>
          <span className={styles.brand}>
            {display?.restaurant.logoUrl ? (
              <img src={display.restaurant.logoUrl} alt="" className={styles.venueLogo} />
            ) : (
              <Logo size={36} />
            )}
          </span>
          {/* Acces permanent aux commandes du repas, en cours comme payees,
              chacune avec son recu. Voir docs/07, section 2.3. */}
          <Link to="/commandes" className={styles.ordersLink}>
            <Icon name="receipt" size={18} />
            {labels.order.myOrders}
          </Link>
        </div>
        <p className={styles.welcome}>{labels.menu.welcome}</p>
        <h1 className={styles.name}>{restaurantName}</h1>
        {display && (
          <p className={styles.meta}>
            <span className={styles.tablePill}>
              <Icon name="table" size={16} />
              {labels.app.tableLabel(display.table.label)}
            </span>
            {display.table.zone && <span>{display.table.zone}</span>}
          </p>
        )}
      </header>

      <div className={`${styles.tabsBar} ${pinned ? styles.tabsPinned : ''}`}>
        {pinned && <span className={styles.pinnedName}>{restaurantName}</span>}
        {!loading && cats.length > 0 && (
          <CategoryTabs label={labels.menu.categories} categories={cats} active={activeCat} onSelect={selectCat} />
        )}
      </div>

      <main className={styles.content}>
        {loading ? (
          <div className={styles.list}>
            <div className="skeleton" style={{ height: '1.5rem', width: '40%' }} />
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="skeleton" style={{ height: '7.25rem', borderRadius: 'var(--radius-lg)' }} />
            ))}
          </div>
        ) : error ? (
          <EmptyState icon="wifi-off" text={error} role="alert">
            <button onClick={() => { setLoading(true); void load(); }} className="btn-primary">
              <Icon name="refresh" size={18} />
              {labels.errors.retry}
            </button>
          </EmptyState>
        ) : cats.length === 0 ? (
          <EmptyState icon="menu" text={labels.menu.empty} />
        ) : (
          cats.map((c, ci) => (
            <section
              key={c.id}
              ref={(el) => { sectionRefs.current[ci] = el; }}
              className={styles.section}
              aria-labelledby={`cat-${c.id}`}
            >
              <header className={styles.sectionHead}>
                <h2 id={`cat-${c.id}`}>{c.name}</h2>
                <span>{labels.menu.dishCount(c.items.length)}</span>
              </header>
              {c.items.length === 0 ? (
                <p className={styles.emptyCategory}>{labels.menu.emptyCategory}</p>
              ) : (
                <div className={styles.list}>
                  {c.items.map((item) => {
                    const line = cart.lines.find((l) => l.menuItemId === item.id);
                    return (
                      <MenuItemCard
                        key={item.id}
                        item={item}
                        quantity={line?.quantity ?? 0}
                        onAdd={() => cart.addLine({ menuItemId: item.id, nameSnapshot: item.name, unitPriceMinor: item.priceMinor, currency: item.currency })}
                        onQuantityChange={(q) => cart.setQuantity(item.id, q)}
                      />
                    );
                  })}
                </div>
              )}
            </section>
          ))
        )}
      </main>

      <CartBar count={itemCount} total={formatMoney(totalMinor, currency)} />
      <BottomNav />
    </div>
  );
}
